import {
  collection,
  query,
  where,
  orderBy,
  getDocs,
  limit,
  startAfter,
  onSnapshot,
  type DocumentSnapshot,
  type QueryConstraint,
  type Unsubscribe,
} from 'firebase/firestore';
import { db } from '../../config';
import { FileExportHistory } from '../../../../types/fileExportHistory';
import { fileExportStatus } from '../../../../constants/enum';

export const fileExportHistoryRef = collection(db, 'fileExportHistory');

/**
 * パターンIDでファイル出力履歴をリアルタイム監視
 */
export const watchFileExportHistories = (
  patternId: string,
  companyId: string,
  callback: (histories: FileExportHistory[]) => void,
  onError?: (error: Error) => void,
): Unsubscribe => {
  const q = query(
    fileExportHistoryRef,
    where('pattern_id', '==', patternId),
    where('company_id', '==', companyId),
    where('status', '==', fileExportStatus.active),
    orderBy('created_at', 'desc'),
  );

  return onSnapshot(
    q,
    snapshot => {
      const histories: FileExportHistory[] = [];
      snapshot.forEach(doc => {
        histories.push({
          id: doc.id,
          ...doc.data(),
        } as FileExportHistory);
      });
      callback(histories);
    },
    error => {
      if (onError) onError(error);
    },
  );
};

/**
 * パターンIDでファイル出力履歴を取得
 */
export const getFileExportHistories = async (
  patternId: string,
  pageSize = 20,
  lastDoc?: DocumentSnapshot,
): Promise<{
  histories: FileExportHistory[];
  lastVisible: DocumentSnapshot | null;
  isLastPage: boolean;
}> => {
  const constraints: QueryConstraint[] = [
    where('pattern_id', '==', patternId),
    where('status', '==', fileExportStatus.active),
    orderBy('created_at', 'desc'),
    limit(pageSize),
  ];

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(fileExportHistoryRef, ...constraints);
  const querySnapshot = await getDocs(q);

  const histories: FileExportHistory[] = [];
  querySnapshot.forEach(doc => {
    histories.push({
      id: doc.id,
      ...doc.data(),
    } as FileExportHistory);
  });

  const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
  const isLastPage = querySnapshot.docs.length < pageSize;

  return {
    histories,
    lastVisible,
    isLastPage,
  };
};

/**
 * 会社IDでファイル出力履歴を取得
 */
export const getFileExportHistoriesByCompany = async (
  companyId: string,
  pageSize = 20,
  lastDoc?: DocumentSnapshot,
): Promise<{
  histories: FileExportHistory[];
  lastVisible: DocumentSnapshot | null;
  isLastPage: boolean;
}> => {
  const constraints: QueryConstraint[] = [
    where('company_id', '==', companyId),
    where('status', '==', fileExportStatus.active),
    orderBy('created_at', 'desc'),
    limit(pageSize),
  ];

  if (lastDoc) {
    constraints.push(startAfter(lastDoc));
  }

  const q = query(fileExportHistoryRef, ...constraints);
  const querySnapshot = await getDocs(q);

  const histories: FileExportHistory[] = [];
  querySnapshot.forEach(doc => {
    histories.push({
      id: doc.id,
      ...doc.data(),
    } as FileExportHistory);
  });

  const lastVisible = querySnapshot.docs[querySnapshot.docs.length - 1] || null;
  const isLastPage = querySnapshot.docs.length < pageSize;

  return {
    histories,
    lastVisible,
    isLastPage,
  };
};
